/**
 * Engagement and Submission.
 *
 * Authority: NIMER-SCOPE-DM-001 §4 (`engagements`, `submissions`) and §5 (the
 * state machine). **DM-001 §5 is authoritative for state**, not PRD §4.3: the
 * DDL CHECK constraint *is* the state machine, and PRD §4.3's `DRAFT` /
 * `SCOPE_APPROVED` vocabulary is a documented defect being corrected under
 * scope-0bg.
 *
 * The `scoped` / `approved` split is load-bearing now that the commercial layer
 * is in scope: `scoped` = scope agreed, `approved` = commercial agreed.
 * Collapsing them renders pricing one state early.
 */
import { z } from 'zod';
import { EngagementRefSchema, NonEmptyTextSchema, OptionKeySchema, SemVerSchema, TimestampSchema, UuidSchema, ShortTextSchema, LongTextSchema, } from './primitives.js';
import { ContactSchema } from './party.js';
import { OpeningOptionSchema, PackKeySchema, ServiceLineSchema } from './service.js';
export const ENGAGEMENT_STATES = [
    'discovery',
    'in_progress',
    'submitted',
    'in_review',
    'clarification',
    'scoped',
    'approved',
    'closed',
];
export const EngagementStateSchema = z.enum(ENGAGEMENT_STATES);
/**
 * Where tier 1 has reached: identify the services, then size them.
 *
 * Authority: PRD §5.5; `docs/design/adaptive-scoping.md` §4;
 * `docs/design/three-ring-questionnaire.md` §2.2.
 *
 * - `classify` — only `classifying` questions are served. The classifier runs
 *   on every answer as it always has. The phase ends at the confirm-scope
 *   checkpoint, where the customer agrees the indicative service list.
 * - `quantify` — only questions belonging to confirmed services are served,
 *   selected by the scoping plan.
 *
 * **Orthogonal to {@link EngagementStateSchema}.** State is the engagement's
 * lifecycle and gates tier 1 against tier 2 (`mobilising` questions are
 * post-`approved`). Phase is a subdivision *within* tier 1. An engagement is
 * `in_progress` in either phase, and collapsing the two would make the
 * confirmation checkpoint a lifecycle transition it is not — the customer is
 * confirming a scope, not submitting one.
 */
export const SCOPING_PHASES = ['classify', 'quantify'];
export const ScopingPhaseSchema = z.enum(SCOPING_PHASES);
/** IMPL-ENGAGE-002 §3.5: how an engagement came into existence. */
export const ENGAGEMENT_ORIGINS = ['consultant', 'lead_conversion', 'self_serve'];
export const EngagementOriginSchema = z.enum(ENGAGEMENT_ORIGINS);
/**
 * 8 characters of Crockford base32 with I, L, O and U removed — I/L/O collide
 * with 1/0 when read aloud or retyped from a printed mail, and U is dropped so
 * the alphabet cannot spell something unfortunate. Minted by
 * `scoping_portal.mint_customer_ref()`; this is the shape, not the source.
 */
export const CustomerRefSchema = z.string().regex(/^[0-9ABCDEFGHJKMNPQRSTVWXYZ]{8}$/);
/**
 * Every legal edge in DM-001 §5, and no others. An illegal transition is a 409
 * `ILLEGAL_STATE_TRANSITION`; the database enforces the same set with a trigger
 * (scope-ibm) so that a handler bug cannot open a path the model forbids.
 *
 * `approved → closed` is an explicit Consultant/Admin close action. DM-001 §5's
 * "proposal issued via existing NIMER document workflow" trigger is out of
 * build scope (PRD §8) and is corrected under scope-0bg.
 */
export const ENGAGEMENT_TRANSITIONS = [
    { from: 'discovery', to: 'in_progress' },
    { from: 'in_progress', to: 'submitted' },
    { from: 'submitted', to: 'in_review' },
    { from: 'in_review', to: 'clarification' },
    { from: 'clarification', to: 'in_review' },
    { from: 'in_review', to: 'scoped' },
    { from: 'scoped', to: 'approved' },
    { from: 'approved', to: 'closed' },
    /*
     * IMPL-ENGAGE-002 §3.6 — engagements must be able to die.
     *
     * There was exactly one arrow into `closed`, from `approved`. A wrong
     * engagement sitting in `discovery` therefore had no legal exit, so it
     * could never leave `engine_login_lookup`'s `state <> 'closed'` filter or
     * the staff queue: it was immortal. Every pre-approval state now closes.
     *
     * Staff-only, reason required (written to `closed_reason`), audited. The
     * database mirrors this set in `engagement_state_edges`, so a change here
     * without the matching migration is rejected by the transition trigger.
     */
    { from: 'discovery', to: 'closed' },
    { from: 'in_progress', to: 'closed' },
    { from: 'submitted', to: 'closed' },
    { from: 'in_review', to: 'closed' },
    { from: 'clarification', to: 'closed' },
    { from: 'scoped', to: 'closed' },
];
/**
 * States in which nothing price-bearing may be rendered, for anyone.
 *
 * PRD §5.9: enforce this in the API layer, not just the UI, so that a client
 * is never asked to omit them. `apps/api/src/lib/statePolicy.ts` is where that
 * stripping lives — but note that **nothing calls it yet**, because the two
 * responses that would carry an EST-001 field (`GET .../scope-package`,
 * `GET .../scope-summary`) are not implemented. This comment previously said
 * "the server strips", present tense, which reads as a control in force; the
 * residue audit found no caller. Wire `stripPriceBearing` into the
 * scope-package handler in the same commit that creates it.
 */
export const PRE_APPROVAL_STATES = [
    'discovery',
    'in_progress',
    'submitted',
    'in_review',
    'clarification',
    'scoped',
];
export const EngagementSchema = z.object({
    id: UuidSchema,
    /**
     * Internal display ref. It is returned to staff surfaces only and never
     * appears in a URL, an API path, or customer-facing email (scope-5ht).
     */
    ref: EngagementRefSchema,
    party_id: UuidSchema,
    state: EngagementStateSchema,
    /** IMPL-SVCLINES-001 §5: the portfolio tier. DB default backfills old rows. */
    service_line: ServiceLineSchema,
    /**
     * IMPL-ENGAGE-002 §3.5. The customer-facing reference — random, never
     * sequential, and NOT a credential: OTP plus a roster row still gate every
     * read. It exists so a person holding two invitation emails can tell which
     * is which. `ref` stays staff-only for the reasons its own comment gives.
     */
    customer_ref: CustomerRefSchema,
    /** How it came into existence (§3.5). */
    origin: EngagementOriginSchema,
    /** Set when this engagement lost a duplicate resolution (§9.2). */
    superseded_by: UuidSchema.nullable(),
    closed_reason: ShortTextSchema.nullable(),
    opening_selection: z.array(OpeningOptionSchema),
    /** Verbatim customer words. Immutable once set (DM-001 §1.2, PRD §5.7). */
    customer_request: LongTextSchema.nullable(),
    /**
     * The organisation, joined from `parties` for the staff queue and the
     * assignments console (mocks 2B, 3B). Both drew an Organisation column that
     * had nothing to render — a reference alone does not tell a consultant
     * whose engagement they are looking at.
     */
    org_name: ShortTextSchema,
    /**
     * The consultant holding it, or null. `null` is a real state — the
     * unassigned queue an Admin works top-down — so it is nullable rather than
     * absent. 3B previously rendered every row as unassigned because this was
     * not on the wire at all.
     *
     * The NAME rather than the staff-table id: both mocks draw "Sara Haddad",
     * and a queue that printed a uuid would be the only surface in the product
     * that did. `assigned_oid` is the Entra object id beside it, so the client
     * can answer "is this mine?" without being handed an internal key.
     */
    assigned_to: ShortTextSchema.nullable(),
    assigned_oid: UuidSchema.nullable(),
    created_at: TimestampSchema,
    updated_at: TimestampSchema,
});
/**
 * The staff console's immutable left column (scope-3r1, scope-ttf).
 *
 * `org_name` and `org_contact` are `parties` columns; `org_sector` and
 * `org_drivers` are `scope_facts` rows (`org.sector`, `org.drivers` —
 * `docs/schemas/NIMER-SCOPE-SCH-GEN-001.md` GEN.ORG.04, GEN.DRV.01), not
 * columns on `engagements` or `parties` — hence `null` / `[]` when the fact
 * has not been answered yet, which is a normal in-progress state and not a
 * loading state.
 *
 * `needs_consultant_discovery` and `can_generate_scope_package` are
 * `apps/api/src/lib/driverRouting.ts`'s `routeDrivers` / `canGenerateScopePackage`
 * read live from `org_drivers` and the engagement's `override_consultant_discovery`
 * review actions (DM-001 §4) — derived on every read, not a stored column, per
 * DM-001 §4's default for a fact with no demonstrated list-scan need (scope-ub34).
 */
export const EngagementDetailSchema = EngagementSchema.extend({
    org_name: NonEmptyTextSchema,
    /**
     * Who the work is FOR, when `org_name` is a partner raising it on someone
     * else's behalf. `null` for a direct customer.
     *
     * The console could not see this at all: a partner engagement rendered
     * identically to a direct one, so a consultant approved a scope
     * commercially without ever being shown whose work it was.
     */
    end_customer_org: NonEmptyTextSchema.nullable(),
    org_contact: ContactSchema,
    org_sector: OptionKeySchema.nullable(),
    org_drivers: z.array(OptionKeySchema),
    needs_consultant_discovery: z.boolean(),
    can_generate_scope_package: z.boolean(),
});
/**
 * `opening_selection` is conditionally required (IMPL-SVCLINES-001 §3.3): the
 * opening question is the ASSESSMENT line's front door and nobody else's, so
 * min(1) applies exactly when `service_line = 'ASSESSMENT'` and the array
 * must be empty otherwise. Enforced with `superRefine` on the unchanged base
 * array — the same pattern as the web's `entryMode.ts` partner gate — never
 * by weakening the base schema.
 */
export const EngagementCreateRequestSchema = z
    .object({
    party_id: UuidSchema,
    service_line: ServiceLineSchema.default('ASSESSMENT'),
    opening_selection: z.array(OpeningOptionSchema),
    customer_request: ShortTextSchema.nullish(),
})
    .superRefine((value, ctx) => {
    if (value.service_line === 'ASSESSMENT' && value.opening_selection.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['opening_selection'], message: 'required' });
    }
    if (value.service_line !== 'ASSESSMENT' && value.opening_selection.length > 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['opening_selection'],
            message: 'must be empty for a non-assessment service line',
        });
    }
});
/** `{"GEN":"1.0.0","APP":"1.0.0"}` — DM-001 §4 `submissions.pack_versions`. */
export const PackVersionsSchema = z.record(PackKeySchema, SemVerSchema);
export const SubmissionSchema = z.object({
    id: UuidSchema,
    engagement_id: UuidSchema,
    pack_versions: PackVersionsSchema,
    started_at: TimestampSchema,
    completed_at: TimestampSchema.nullable(),
});
/**
 * A state change requested by a staff surface. The actor comes from the
 * session, never from the body.
 */
export const EngagementTransitionRequestSchema = z.object({
    to: EngagementStateSchema,
    reason: ShortTextSchema.nullish(),
});
//# sourceMappingURL=engagement.js.map